import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtDateOnly as fmtDate } from "@/lib/datetime";
import { Plus, Pencil, Loader2, CalendarRange, Trash2 } from "lucide-react";

const EMPTY = { name: "", start_date: "", end_date: "", active: false };

function SemesterDialog({ open, onOpenChange, semester, onSaved }) {
  const isEdit = !!semester;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        semester
          ? {
              name: semester.name || "",
              start_date: semester.start_date || "",
              end_date: semester.end_date || "",
              active: semester.active ?? false,
            }
          : EMPTY
      );
    }
  }, [open, semester]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/semesters/${semester.id}`, form);
        toast.success("Semester updated");
      } else {
        await api.post("/admin/semesters", form);
        toast.success("Semester created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit semester" : "Add semester"}</DialogTitle>
          <DialogDescription>
            Class days (Mon &amp; Thu) inside the active semester drive derived attendance.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="semester-form">
          <div className="space-y-2">
            <Label htmlFor="sem-name">Name</Label>
            <Input
              id="sem-name"
              data-testid="semester-name-input"
              className="h-11"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="sem-start">Start date</Label>
              <Input
                id="sem-start"
                data-testid="semester-start-input"
                type="date"
                className="h-11"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sem-end">End date</Label>
              <Input
                id="sem-end"
                data-testid="semester-end-input"
                type="date"
                className="h-11"
                value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)}
                required
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium">Active semester</p>
              <p className="text-xs text-muted-foreground">Only one can be active at a time.</p>
            </div>
            <Switch
              checked={form.active}
              onCheckedChange={(v) => set("active", v)}
              data-testid="semester-active-switch"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="h-11" data-testid="semester-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create semester"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Semesters() {
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/semesters");
      setSemesters(data);
    } catch (e) {
      toast.error("Failed to load semesters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/semesters/${deleting.id}`);
      toast.success("Semester deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-5xl" data-testid="semesters-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
            Semesters
          </h1>
          <p className="mt-1 text-muted-foreground">
            Define semester windows. Class days are Monday &amp; Thursday.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          className="h-11"
          data-testid="add-semester-button"
        >
          <Plus className="h-4 w-4 mr-2" /> Add semester
        </Button>
      </div>

      <div className="mt-6 bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : semesters.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="semesters-empty">
            <CalendarRange className="h-8 w-8 mx-auto mb-3 opacity-40" />
            No semesters yet. Add your first semester.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {semesters.map((s) => (
                  <TableRow key={s.id} data-testid={`semester-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(s.start_date)}</TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(s.end_date)}</TableCell>
                    <TableCell>
                      {s.active ? (
                        <Badge className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))] text-white" data-testid={`semester-active-${s.id}`}>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(s);
                          setDialogOpen(true);
                        }}
                        data-testid={`edit-semester-${s.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleting(s)}
                        data-testid={`delete-semester-${s.id}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <SemesterDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        semester={editing}
        onSaved={load}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete semester?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" will be removed. Daily records are kept, but attendance for
              this window will no longer be calculated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-semester">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
